import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingEvent, useLocalization } from '@ui-framework/index';

const MAX_ROWS = 100;

/**
 * Parse CSV text into rows of fields (RFC4180-style: quoted fields, commas, newlines).
 */
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || (char === '\r' && next === '\n')) {
      row.push(field);
      field = '';
      if (row.some(cell => cell !== '') || rows.length > 0) {
        rows.push(row);
      }
      row = [];
      if (char === '\r') {
        i += 1;
      }
    } else if (char !== '\r') {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
}

function CsvViewer({ csvUrl }) {
  const { formatMessage } = useLocalization();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [raw, setRaw] = useState('');
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setRaw('');
      setRows([]);
      setTruncated(false);

      try {
        const response = await fetch(csvUrl);
        if (!response.ok) {
          throw new Error(`Failed to load CSV (${response.status})`);
        }
        const text = await response.text();
        if (cancelled) return;

        const parsed = parseCsv(text);
        const limited = parsed.slice(0, MAX_ROWS + 1); // +1 for header
        setRaw(text);
        setRows(limited);
        setTruncated(parsed.length > MAX_ROWS + 1);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load CSV');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (csvUrl) {
      load();
    } else {
      setLoading(false);
      setError('No CSV URL provided');
    }

    return () => {
      cancelled = true;
    };
  }, [csvUrl]);

  const tableLabel = formatMessage({
    id: 'VIEWER_CSV_TABLE',
    defaultMessage: 'Table',
  });
  const rawLabel = formatMessage({
    id: 'VIEWER_CSV_RAW',
    defaultMessage: 'CSV',
  });

  if (loading) {
    return <LoadingEvent>Processing...</LoadingEvent>;
  }

  if (error) {
    return <Typography>{error}</Typography>;
  }

  const headers = rows[0] || [];
  const bodyRows = rows.slice(1);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          px: 1,
          py: 0.5,
        }}>
        <Button
          role="tab"
          size="small"
          variant={tab === 0 ? 'contained' : 'text'}
          onClick={() => setTab(0)}>
          {tableLabel}
        </Button>
        <Button
          role="tab"
          size="small"
          variant={tab === 1 ? 'contained' : 'text'}
          onClick={() => setTab(1)}>
          {rawLabel}
        </Button>
      </Box>

      {tab === 0 && (
        <Box
          sx={{
            p: 1.5,
            maxHeight: 'min(70vh, 720px)',
            overflow: 'auto',
          }}>
          {truncated && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}>
              Showing first {MAX_ROWS} rows
            </Typography>
          )}
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {/* CSV columns have no stable ids; index keys are intentional */}
                {/* eslint-disable react/no-array-index-key */}
                {headers.map((header, index) => (
                  <TableCell
                    key={`h-${index}`}
                    sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {header}
                  </TableCell>
                ))}
                {/* eslint-enable react/no-array-index-key */}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* eslint-disable react/no-array-index-key */}
              {bodyRows.map((row, rowIndex) => (
                <TableRow key={`r-${rowIndex}`}>
                  {headers.map((_, colIndex) => (
                    <TableCell
                      key={`c-${rowIndex}-${colIndex}`}
                      sx={{ whiteSpace: 'nowrap' }}>
                      {row[colIndex] ?? ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {/* eslint-enable react/no-array-index-key */}
            </TableBody>
          </Table>
        </Box>
      )}

      {tab === 1 && (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            maxHeight: 'min(70vh, 720px)',
            overflow: 'auto',
            fontSize: theme => theme.typography.caption.fontSize,
            fontFamily: 'ui-monospace, monospace',
          }}>
          {raw}
        </Box>
      )}
    </Paper>
  );
}

export default CsvViewer;
